import {
	faCheck,
	faChevronDown,
	faMagnifyingGlass,
	faSpinner,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Listbox, Transition } from '@headlessui/react';
import React, {
	Fragment,
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
	getKeyboardMenuMaxHeight,
	useKeyboardAwareFloating,
} from '../../hooks/useKeyboardAwareFloating';
import { useLayerZIndex } from '../../providers/LayerProvider';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { resolveAutofillOption } from '../../utils/select-autofill';
import { filterSelectOptions } from '../../utils/select-option-search';

interface SelectOption {
	value: string | number;
	label: string;
	disabled?: boolean;
	/**
	 * PD-15878 — a mark rendered before the label, in the closed control as
	 * well as in the menu. Added for payment methods, where a card's network
	 * is recognized from its logo long before the four digits are read;
	 * anything that identifies an option at a glance belongs here.
	 *
	 * Optional and inert when absent, so every existing caller is unchanged.
	 */
	icon?: React.ReactNode;
	/**
	 * PD-16381 — a second, smaller line under the label in the open menu, for
	 * the details that help choose between options but crowd the label when
	 * run onto it (a payment method's funding type, fee and account). Same
	 * name and role as `SelectSearchOption.subtitle`. Shown in the menu only;
	 * the closed control keeps its single-line height. Optional and inert when
	 * absent.
	 */
	subtitle?: string;
	/**
	 * PD-16381 — short text kept visible after the label in the CLOSED control
	 * once this option is chosen (a payment method's fee). The label truncates
	 * before it does, so it survives a narrow control. Optional and inert when
	 * absent.
	 */
	selectedSuffix?: string;
	/**
	 * PD-17144 — a heading this option sits under in the open menu, the
	 * Listbox equivalent of a native `<optgroup>`. A heading is drawn before
	 * the first option of each run of consecutive options sharing a group, so
	 * callers pass options already ordered by group. Options without a group
	 * render as before. Optional and inert when absent.
	 */
	group?: string;
}

// `size` is omitted along with `onChange` — this component repurposes
// `size` as a design-size token, not the native row count.
interface SelectProps
	extends Omit<
		React.SelectHTMLAttributes<HTMLSelectElement>,
		'onChange' | 'size'
	> {
	options: SelectOption[];
	placeholder?: string;
	error?: boolean;
	fullWidth?: boolean;
	onChange?: (value: string) => void;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	allowClear?: boolean;
	onOpen?: () => void;
	isLoading?: boolean;
	loadingText?: string;
	/**
	 * PD-16249 — a WHATWG autofill token (`address-level1`, `country`, …).
	 * Setting it renders a hidden native input the browser can fill; the text
	 * it fills is matched onto an option by value or label. Left unset, the
	 * control is a plain Listbox with nothing for autofill to target.
	 */
	autoComplete?: string;
	/**
	 * PD-15027 — a search field at the top of the open menu that narrows the
	 * options as the user types: every typed word must appear in an option's
	 * label or subtitle, ignoring case, and group headings stay above the
	 * matches that remain. Arrow keys move through the matches and Enter picks
	 * one. Meant for lists long enough that scanning is slower than typing (a
	 * customer's many locations). Off by default, and when off nothing about
	 * the control changes.
	 */
	searchable?: boolean;
	/**
	 * Placeholder (and accessible name) of the search field, already
	 * translated like `placeholder`. Defaults to "Search...".
	 */
	searchPlaceholder?: string;
}

export function Select({
	options,
	placeholder,
	error = false,
	fullWidth = true,
	onChange,
	disabled,
	className,
	value,
	size = 'md',
	allowClear = false,
	onOpen,
	isLoading = false,
	loadingText = 'Loading...',
	'aria-label': ariaLabel,
	'aria-labelledby': ariaLabelledBy,
	required = false,
	name,
	autoComplete,
	searchable = false,
	searchPlaceholder,
}: SelectProps) {
	const colors = useThemeColors();
	const wasOpenRef = useRef(false);
	// PD-15027 — ids the searchable menu needs: its search field names the
	// listbox it controls and the option it has highlighted.
	const idPrefix = useId();
	const listboxId = `${idPrefix}-listbox`;
	// The menu portals to <body>, so it competes with every other overlay at
	// the top of the page. 1000 clears a Modal or Sidebar; inside a Popover
	// (3000) or any other overlay above that, the menu is lifted above the
	// overlay it was opened from instead of opening underneath it (PD-16154).
	const menuZIndex = useLayerZIndex(1000);

	// Floating UI to render the menu in a portal at body-level so it
	// escapes any ancestor `overflow-auto` (sidebars, scroll containers)
	// that would otherwise clip the dropdown. Mirrors the SelectSearch
	// pattern used elsewhere in this codebase.
	//
	// PD-16133 — the menu also measures itself rather than inheriting the
	// control's width. A narrow control (a duration picker in a row of small
	// controls, a toolbar select) produced a menu as narrow as itself, in which
	// every option was truncated to a fragment — "15 min" read as "1…". The
	// menu is now at least as wide as the control and grows to its widest
	// option, capped to what the viewport can show, which is what a native
	// select does. `size` also caps the height to the space available, so a
	// long list near the foot of the window scrolls instead of running off it.
	//
	// PD-14097 — the shared hook keeps the menu above the native app's
	// keyboard. While the keyboard is up the 120px floor gives way to the
	// room actually left above it, so the options are not hidden behind it.
	const { x, y, strategy, refs } = useKeyboardAwareFloating({
		applySize({
			rects,
			availableWidth,
			availableHeight,
			elements,
			keyboardHeight,
		}) {
			const maxHeight =
				keyboardHeight > 0
					? getKeyboardMenuMaxHeight(availableHeight, 240)
					: Math.max(120, Math.min(240, availableHeight));
			Object.assign(elements.floating.style, {
				minWidth: `${String(rects.reference.width)}px`,
				maxWidth: `${String(Math.max(rects.reference.width, availableWidth))}px`,
				maxHeight: `${String(maxHeight)}px`,
			});
		},
	});

	// Find the selected option
	const selectedOption = options?.find((opt) => opt.value === value);

	// Size-based styling to match MultiSelect
	const sizeClasses = {
		xs: 'px-2.5 py-1 text-xs h-[var(--control-h-xs,2rem)]',
		sm: 'px-3 py-1.5 text-sm min-h-[30px]',
		md: 'px-3 py-2 text-sm h-[var(--control-h,2.25rem)]',
		lg: 'px-4 py-2.5 text-base min-h-[42px]',
	}[size];

	// Size-based styling for dropdown options
	const optionSizeClasses = {
		xs: 'py-1 pl-2.5 pr-7 text-xs',
		sm: 'py-1.5 pl-3 pr-7 text-sm',
		md: 'py-1.5 pl-3 pr-7 text-sm',
		lg: 'py-2 pl-4 pr-8 text-base',
	}[size];

	// The option and heading markup, shared by the plain menu and the
	// searchable one (PD-15027) so the two cannot drift apart. `active` is the
	// highlighted row: Headless UI's own in the plain menu, the search
	// field's in the searchable one.
	const placeholderClassName = (active: boolean): string =>
		cn(
			'relative cursor-pointer transition-colors',
			optionSizeClasses,
			active && colors.classes.selectOption,
			!active && colors.classes.textSecondary,
		);

	const optionClassName = (
		option: SelectOption,
		active: boolean,
		selected: boolean,
	): string =>
		cn(
			'relative transition-colors',
			optionSizeClasses,
			option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
			selected && !active && colors.classes.selectOptionSelected,
			active && colors.classes.selectOption,
			!selected && !active && colors.classes.textPrimary,
		);

	const renderGroupHeading = (
		group: string,
		onMouseDown?: (event: React.MouseEvent) => void,
	): React.ReactNode => (
		// PD-17144 — a heading, not an option: Listbox skips it for keyboard
		// navigation and selection.
		<li
			role="presentation"
			onMouseDown={onMouseDown}
			className={cn(
				'list-none px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide cursor-default select-none',
				colors.classes.textSecondary,
			)}
		>
			{group}
		</li>
	);

	const renderOptionContent = (
		option: SelectOption,
		selected: boolean,
	): React.ReactElement => (
		<>
			<span className="flex items-center gap-2 min-w-0">
				{option.icon != null && (
					<span className="shrink-0 inline-flex items-center leading-none">
						{option.icon}
					</span>
				)}
				{option.subtitle != null && option.subtitle !== '' ? (
					<span className="flex min-w-0 flex-col">
						<span
							className={cn(
								'block truncate',
								selected ? 'font-medium' : 'font-normal',
							)}
						>
							{option.label}
						</span>
						<span
							className={cn(
								'block truncate text-xs',
								colors.classes.textSecondary,
							)}
						>
							{option.subtitle}
						</span>
					</span>
				) : (
					<span
						className={cn(
							'block truncate',
							selected ? 'font-medium' : 'font-normal',
						)}
					>
						{option.label}
					</span>
				)}
			</span>
			{selected && (
				<span
					className={cn(
						'absolute inset-y-0 right-0 flex items-center pr-1.5',
						colors.classes.textPrimary,
					)}
				>
					<FontAwesomeIcon icon={faCheck} className="h-3 w-3" aria-hidden="true" />
				</span>
			)}
		</>
	);

	const handleChange = useCallback(
		(optionValue: string | number) => {
			onChange?.(String(optionValue));
		},
		[onChange],
	);

	const handleClear = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			onChange?.('');
		},
		[onChange],
	);

	// PD-16249 — the browser hands over free text (a saved state as "CA" or
	// "California", in any casing); select the option it names, or leave the
	// control alone when nothing matches.
	const handleAutofill = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			// The browser hands over free text — a saved state arrives as "MI"
			// or as "Michigan", in any casing — so it is matched onto an option
			// by value or by label, and ignored when it matches neither.
			const match = resolveAutofillOption(options, event.target.value);
			if (match != null) {
				onChange?.(String(match.value));
			}
		},
		[onChange, options],
	);

	const hasValue = value != null && value !== '';
	const showClearButton = allowClear && hasValue && !disabled;

	// PD-16133 — the room kept on the right for the chevron (and the clear
	// mark). The chevron needs 20px; `pr-10` reserved 40px of a control only
	// 60px wide, so an xs control clipped its own value ("30 min" read as
	// "30 mi"). The larger sizes keep the roomier padding they had.
	const trailingPadding =
		size === 'xs'
			? showClearButton
				? 'pr-12'
				: 'pr-7'
			: showClearButton
				? 'pr-16'
				: 'pr-10';

	return (
		<Listbox value={value ?? ''} onChange={handleChange} disabled={disabled}>
			{({ open }) => {
				// Call onOpen when dropdown opens
				if (open && !wasOpenRef.current && onOpen != null) {
					// Use setTimeout to avoid calling during render
					setTimeout(() => {
						onOpen();
					}, 0);
				}
				wasOpenRef.current = open;

				return (
					<div className={cn('relative', fullWidth ? 'w-full' : 'w-auto')}>
						<Listbox.Button
							ref={refs.setReference}
							// Toolbar selects often have no visible label; forward the
							// aria naming so assistive technology and tests can find them.
							aria-label={ariaLabel}
							aria-required={required || undefined}
							aria-labelledby={ariaLabelledBy}
							className={cn(
								// Base styling
								'relative w-full cursor-pointer text-left',

								// Size-based padding and text
								sizeClasses,

								// The room kept on the right for the chevron. It follows
								// the size classes: `cn` merges conflicting Tailwind
								// padding utilities by keeping the last one, and `px-*`
								// listed after it used to cancel it, which is why the
								// chevron sat on top of the value (PD-16133).
								trailingPadding,

								// Use form theme classes
								colors.classes.formFieldBase,

								// State-based styling
								error
									? colors.classes.formFieldError
									: colors.classes.formFieldDefault,

								// Focus state
								colors.classes.formFieldFocus,

								disabled && colors.classes.formFieldDisabled,

								fullWidth && 'w-full',
								className,
							)}
						>
							<span className="flex items-center gap-2 min-w-0">
								{selectedOption?.icon != null && (
									<span className="shrink-0 inline-flex items-center leading-none">
										{selectedOption.icon}
									</span>
								)}
								<span
									className={cn(
										'block truncate',
										// PD-16249 — the same tone a native input paints its
										// placeholder in, so a dropdown and the fields beside
										// it read as one set of controls.
										!selectedOption && placeholder && 'text-subtle',
									)}
								>
									{selectedOption
										? selectedOption.label
										: placeholder || 'Select...'}
								</span>
								{selectedOption?.selectedSuffix != null &&
									selectedOption.selectedSuffix !== '' && (
										<span
											className={cn(
												'shrink-0 whitespace-nowrap text-xs',
												colors.classes.textSecondary,
											)}
										>
											{selectedOption.selectedSuffix}
										</span>
									)}
							</span>

							{/* Clear button — matches BaseTextInput's clear: just a
							    color transition on the icon, no background. */}
							{showClearButton && (
								<div
									role="button"
									tabIndex={0}
									onClick={handleClear}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											handleClear(e as any);
										}
									}}
									className={cn(
										'absolute inset-y-0 right-8 flex items-center justify-center w-6 cursor-pointer',
									)}
								>
									<FontAwesomeIcon
										icon={faXmark}
										className={cn(
											'h-3 w-3',
											colors.classes.textSecondary,
											'hover:' + colors.classes.textPrimary,
											'transition-colors',
										)}
										aria-hidden="true"
									/>
								</div>
							)}

							{/* Dropdown chevron */}
							<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
								<FontAwesomeIcon
									icon={faChevronDown}
									// PD-16249 — the quiet tone the placeholder uses, so the
									// affordance does not read louder than the value itself.
									className={cn('h-3 w-3 text-subtle')}
									aria-hidden="true"
								/>
							</span>
						</Listbox.Button>

						{autoComplete != null && (
							// PD-16249 — browser autofill writes only into native form
							// controls, and a Listbox is a button plus a menu, so a state
							// dropdown never received the saved address that filled the
							// inputs around it. This input exists purely as the browser's
							// fill target; whatever text it receives is matched onto an
							// option by `handleAutofill`.
							//
							// A native select was tried first, on the reasoning that a
							// browser would match a saved "MI" or "Michigan" against the
							// options itself. It fills either shape equally well, but a
							// select is painted differently: the pale wash a browser puts
							// on a field it is previewing or has filled is drawn outside
							// normal element rendering, so neither layering nor a filter
							// could keep it off the control. On an input that wash is an
							// ordinary background and disappears with the rest of it.
							//
							// It sits behind the button, which paints an opaque surface of
							// its own, and paints nothing itself. The element keeps the
							// control's full size and its `opacity` and `visibility`
							// untouched, because those are what a browser reads when it
							// decides whether a field can be filled at all; a filter is
							// applied later, when the pixels are drawn. It stays out of the
							// tab order, the accessibility tree and pointer events, so the
							// Listbox button remains the control the user operates.
							<input
								// Marks this as the browser's fill target rather than a
								// control of its own, so an app's global styling for native
								// fields — including the rules that repaint a filled one —
								// leaves it alone. It must paint nothing in any state.
								data-autofill-proxy="true"
								type="text"
								tabIndex={-1}
								aria-hidden="true"
								name={name}
								autoComplete={autoComplete}
								disabled={disabled}
								value={
									selectedOption != null ? String(selectedOption.value) : ''
								}
								onChange={handleAutofill}
								className="absolute inset-0 h-full w-full pointer-events-none"
								// Written inline because every app styles bare fields
								// globally — a border, a surface background, padding and a
								// shadow — and an element rule outranks a utility class, so
								// the overlay painted itself over the button and hid its
								// label and chevron. An inline style beats an element rule
								// wherever this renders.
								style={{
									zIndex: -1,
									filter: 'opacity(0)',
									appearance: 'none',
									background: 'transparent',
									color: 'transparent',
									// What actually paints the glyphs. A browser marks a
									// field it filled with its own `!important` text color,
									// which outranks `color`; nothing sets this.
									WebkitTextFillColor: 'transparent',
									caretColor: 'transparent',
									border: 0,
									borderRadius: 0,
									boxShadow: 'none',
									outline: 'none',
									padding: 0,
									margin: 0,
								}}
							/>
						)}

						{createPortal(
							<Transition
								as={Fragment}
								show={open}
								leave="transition ease-in duration-100"
								leaveFrom="opacity-100"
								leaveTo="opacity-0"
							>
								<Listbox.Options
									ref={refs.setFloating}
									static
									// PD-15027 — named so the search field can point at it.
									// Undefined keeps Headless UI's generated id.
									id={searchable ? listboxId : undefined}
									// PD-15878 — a dropdown is not a modal. Headless UI v2 makes an
									// open listbox modal by default: it locks scrolling on <html> and
									// marks everything outside the list inert. Inside the CRM that is
									// invisible because the pages scroll in an inner container, but
									// on a body-scrolled page (the storefront) the lock re-homes the
									// scroll to <body>, which blanks the viewport behind the list,
									// pads <html> by a scrollbar width so the page shifts, and drops
									// the scroll position when the list closes. A native <select>
									// does none of that, and neither should this.
									modal={false}
									style={{
										position: strategy,
										top: y ?? 0,
										left: x ?? 0,
										// The control's width is the floor, not the ceiling:
										// `size` above sets minWidth from the control and the
										// caps, and the list takes the width its options need
										// (PD-16133).
										width: 'max-content',
										zIndex: menuZIndex,
									}}
									className={cn(
										// PD-15027 — the searchable menu keeps its search field
										// in place and scrolls only the options beneath it.
										searchable
											? 'flex flex-col overflow-hidden rounded-md text-base sm:text-sm'
											: 'overflow-auto custom-scrollbar rounded-md py-1 text-base sm:text-sm',
										colors.classes.selectMenu,
									)}
								>
									{isLoading ? (
										<div
											className={cn(
												'relative cursor-default py-2 px-3 text-center',
												colors.classes.textSecondary,
											)}
										>
											<FontAwesomeIcon
												icon={faSpinner}
												className="animate-spin mr-2"
											/>
											{loadingText}
										</div>
									) : searchable ? (
										<SearchableSelectMenu
											options={options ?? []}
											value={value}
											placeholder={
												placeholder != null && !required
													? placeholder
													: undefined
											}
											searchPlaceholder={searchPlaceholder}
											idPrefix={idPrefix}
											listboxId={listboxId}
											placeholderClassName={placeholderClassName}
											optionClassName={optionClassName}
											renderGroupHeading={renderGroupHeading}
											renderOptionContent={renderOptionContent}
										/>
									) : (
										<>
											{/* A required field shows its placeholder only as the
											    empty state: offering it as a choice would let the
											    user "pick" nothing. */}
											{placeholder != null && !required && (
												<Listbox.Option
													key="__placeholder__"
													value=""
													className={({ active }) =>
														placeholderClassName(active)
													}
												>
													{placeholder}
												</Listbox.Option>
											)}
											{(options ?? []).map((option, index) => (
												<React.Fragment key={option.value}>
													{option.group != null &&
														option.group !== '' &&
														option.group !== options[index - 1]?.group &&
														renderGroupHeading(option.group)}
													<Listbox.Option
														value={option.value}
														disabled={option.disabled}
														className={({ active, selected }) =>
															optionClassName(option, active, selected)
														}
													>
														{({ selected }) =>
															renderOptionContent(option, selected)
														}
													</Listbox.Option>
												</React.Fragment>
											))}
										</>
									)}
								</Listbox.Options>
							</Transition>,
							document.body,
						)}
					</div>
				);
			}}
		</Listbox>
	);
}

interface SearchableSelectMenuProps {
	options: Array<SelectOption>;
	value: SelectProps['value'];
	/** The "choose nothing" row; undefined when the field offers none. */
	placeholder?: string;
	searchPlaceholder?: string;
	idPrefix: string;
	listboxId: string;
	placeholderClassName: (active: boolean) => string;
	optionClassName: (
		option: SelectOption,
		active: boolean,
		selected: boolean,
	) => string;
	renderGroupHeading: (
		group: string,
		onMouseDown?: (event: React.MouseEvent) => void,
	) => React.ReactNode;
	renderOptionContent: (
		option: SelectOption,
		selected: boolean,
	) => React.ReactElement;
}

interface SearchableRowModel {
	id: string;
	value: string | number;
}

/**
 * Keeps focus in the search field when the pointer goes down on something in
 * the menu that is not an option (the field's padding, a group heading, the
 * "No matches" line). The listbox itself is focusable, so without this a
 * click there moved focus off the field and typing stopped narrowing.
 */
const keepSearchFocus = (event: React.MouseEvent): void => {
	if (event.target === event.currentTarget) {
		event.preventDefault();
	}
};

/**
 * PD-15027 — the contents of a `searchable` Select's open menu: a search
 * field above the options, narrowing them as the user types.
 *
 * Mounted only while the menu is open, so the typed text and the highlight
 * reset every time it closes.
 *
 * Headless UI's Listbox assumes its options never change while it is open:
 * when an option unmounts, its record of the highlighted option is not
 * adjusted, so Enter could pick a different option than the one highlighted,
 * or none. So options that do not match are kept mounted, hidden and
 * disabled, and the search field owns the highlight: it moves through the
 * visible matches with the arrow keys, paints the highlight itself, and on
 * Enter clicks the highlighted option, which selects it and closes the menu
 * exactly as a pointer click does. Escape and Tab still reach the Listbox,
 * which closes the menu and puts focus back on the control; every other key
 * stays in the field, so typing never triggers the Listbox's own
 * jump-to-letter search and Space types a space instead of choosing.
 */
function SearchableSelectMenu({
	options,
	value,
	placeholder,
	searchPlaceholder,
	idPrefix,
	listboxId,
	placeholderClassName,
	optionClassName,
	renderGroupHeading,
	renderOptionContent,
}: SearchableSelectMenuProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const [query, setQuery] = useState('');
	// Arrives translated, like `placeholder`; only the default is ours.
	const searchLabel = searchPlaceholder ?? t('Search...');

	const rows = filterSelectOptions(options, query);
	const rowByIndex = new Map(rows.map((row) => [row.index, row]));
	const hasQuery = query.trim() !== '';
	// The "choose nothing" row only makes sense before anything is typed.
	const showPlaceholder = placeholder != null && !hasQuery;
	const placeholderId = `${idPrefix}-placeholder`;
	const optionId = (index: number): string => `${idPrefix}-option-${index}`;

	// The rows the arrow keys and Enter can reach, in menu order.
	const navigable: Array<SearchableRowModel> = [
		...(showPlaceholder ? [{ id: placeholderId, value: '' }] : []),
		...rows
			.filter((row) => row.option.disabled !== true)
			.map((row) => ({ id: optionId(row.index), value: row.option.value })),
	];

	// Opens on the current value, like the plain menu. Null means "the first
	// match", which is where typing moves the highlight.
	const [activeId, setActiveId] = useState<string | null>(
		() => navigable.find((row) => row.value === value)?.id ?? null,
	);
	const active =
		navigable.find((row) => row.id === activeId) ?? navigable[0] ?? null;

	useEffect(() => {
		// On a phone or tablet the menu opens without raising the on-screen
		// keyboard over half of it; a tap in the field starts a search.
		if (window.matchMedia?.('(pointer: coarse)').matches === true) {
			return undefined;
		}
		// Headless UI focuses the listbox as it opens; take focus into the
		// field after that, on the next frame.
		const frame = requestAnimationFrame(() => {
			inputRef.current?.focus({ preventScroll: true });
		});
		return (): void => {
			cancelAnimationFrame(frame);
		};
	}, []);

	const highlight = (row: SearchableRowModel | undefined): void => {
		if (row == null) {
			return;
		}
		setActiveId(row.id);
		document.getElementById(row.id)?.scrollIntoView({ block: 'nearest' });
	};

	const handleKeyDown = (
		event: React.KeyboardEvent<HTMLInputElement>,
	): void => {
		// Keys that compose a character (an IME's Enter, arrows choosing a
		// candidate) belong to the field.
		if (event.nativeEvent.isComposing) {
			event.stopPropagation();
			return;
		}
		const position =
			active == null ? -1 : navigable.findIndex((row) => row.id === active.id);
		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				event.stopPropagation();
				highlight(navigable[Math.min(position + 1, navigable.length - 1)]);
				return;
			case 'ArrowUp':
				event.preventDefault();
				event.stopPropagation();
				highlight(navigable[Math.max(position - 1, 0)]);
				return;
			case 'PageDown':
				event.preventDefault();
				event.stopPropagation();
				highlight(navigable[navigable.length - 1]);
				return;
			case 'PageUp':
				event.preventDefault();
				event.stopPropagation();
				highlight(navigable[0]);
				return;
			case 'Enter':
				event.preventDefault();
				event.stopPropagation();
				if (active != null) {
					document.getElementById(active.id)?.click();
				}
				return;
			case 'Escape':
			case 'Tab':
				// The Listbox closes the menu and returns focus to the control.
				return;
			default:
				event.stopPropagation();
		}
	};

	return (
		<>
			<div
				className="shrink-0 border-b border-inherit p-2"
				onMouseDown={keepSearchFocus}
			>
				<div className="relative" onMouseDown={keepSearchFocus}>
					<FontAwesomeIcon
						icon={faMagnifyingGlass}
						className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-subtle"
						aria-hidden="true"
					/>
					<input
						ref={inputRef}
						type="text"
						value={query}
						onChange={(event) => {
							setQuery(event.target.value);
							setActiveId(null);
							if (listRef.current != null) {
								listRef.current.scrollTop = 0;
							}
						}}
						onKeyDown={handleKeyDown}
						placeholder={searchLabel}
						aria-label={searchLabel}
						aria-controls={listboxId}
						aria-activedescendant={active?.id}
						aria-autocomplete="list"
						autoComplete="off"
						spellCheck={false}
						className={cn(
							// `brand-field` lets the storefront paint its own focus
							// ring on this field instead of the CRM's. `has-icon-left`
							// opts out of index.css's default input padding, which
							// otherwise puts the text under the search icon.
							'brand-field has-icon-left block w-full py-1.5 pl-8 pr-3 text-sm',
							colors.classes.formFieldBase,
							colors.classes.formFieldDefault,
							colors.classes.formFieldFocus,
						)}
					/>
				</div>
			</div>
			<div
				ref={listRef}
				className="min-h-0 overflow-auto custom-scrollbar py-1"
			>
				{showPlaceholder && (
					<Listbox.Option
						key="__placeholder__"
						id={placeholderId}
						value=""
						onMouseMove={() => {
							if (active?.id !== placeholderId) {
								setActiveId(placeholderId);
							}
						}}
						className={placeholderClassName(active?.id === placeholderId)}
					>
						{placeholder}
					</Listbox.Option>
				)}
				{options.map((option, index) => {
					const row = rowByIndex.get(index);
					const id = optionId(index);
					const reachable = row != null && option.disabled !== true;
					return (
						<React.Fragment key={option.value}>
							{row?.heading != null &&
								renderGroupHeading(row.heading, keepSearchFocus)}
							<Listbox.Option
								id={id}
								value={option.value}
								// A non-match stays mounted (see above) but cannot be
								// seen or reached.
								disabled={option.disabled === true || row == null}
								hidden={row == null}
								onMouseMove={() => {
									if (reachable && active?.id !== id) {
										setActiveId(id);
									}
								}}
								className={({ selected }) =>
									optionClassName(option, active?.id === id, selected)
								}
							>
								{({ selected }) => renderOptionContent(option, selected)}
							</Listbox.Option>
						</React.Fragment>
					);
				})}
				{hasQuery && rows.length === 0 && (
					<div
						role="presentation"
						onMouseDown={keepSearchFocus}
						className={cn(
							'cursor-default select-none px-3 py-2 text-sm',
							colors.classes.textSecondary,
						)}
					>
						{t('No matches')}
					</div>
				)}
			</div>
		</>
	);
}
